import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useImperativeHandle, useRef, useState, } from "react";
import { QRCode } from "react-qrcode-logo";
import { toBlob } from "html-to-image";
import "./_style.scss";
const QR_CODE_SIZE = 190;
const SAMPLE_SIZE = 64;
const FONT_FETCH_TIMEOUT_MS = 5000;
// Must match the "Source Sans Pro" @font-face src in base/_fonts.scss. html-to-image
// rasterizes the card as an offscreen SVG image, which can't see fonts the page has already
// loaded, so the font has to be embedded as a self-contained data URI for the export to use it.
const HEADLINE_FONT_URL = "https://fonts.gstatic.com/s/sourcesanspro/v10/toadOcfmlt9b38dHJxOBGIq-Fb0zf838trI74uojZQY.woff2";
const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i += 1) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
};
let fontEmbedCSSPromise = null;
function getFontEmbedCSS() {
    if (!fontEmbedCSSPromise) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), FONT_FETCH_TIMEOUT_MS);
        fontEmbedCSSPromise = fetch(HEADLINE_FONT_URL, { signal: controller.signal })
            .finally(() => clearTimeout(timeoutId))
            .then((response) => {
            if (!response.ok) {
                throw new Error(`Failed to fetch font: ${response.status}`);
            }
            return response.arrayBuffer();
        })
            .then((buffer) => `data:font/woff2;base64,${arrayBufferToBase64(buffer)}`)
            .then((dataUrl) => `
        @font-face {
          font-family: "Source Sans Pro";
          font-style: normal;
          font-weight: 900;
          src: url(${dataUrl}) format("woff2");
        }
      `)
            .catch(() => {
            // Keep the cache empty so a transient failure doesn't stick for the page's lifetime.
            fontEmbedCSSPromise = null;
            return "";
        });
    }
    return fontEmbedCSSPromise;
}
// Cool (blue-dominant) photos read best with a pink accent, and warm photos read best with blue —
// picking the opposite of the photo's dominant channel keeps the headline legible against any image.
const pickAccentColor = (data) => {
    let red = 0;
    let blue = 0;
    for (let i = 0; i < data.length; i += 4) {
        red += data[i];
        blue += data[i + 2];
    }
    return blue > red ? "pink" : "blue";
};
const ShareImageCard = forwardRef(({ config }, ref) => {
    const cardRef = useRef(null);
    const [accentColor, setAccentColor] = useState("blue");
    const postClass = config.isPost ? " --post" : "";
    // toBlob()/download() must not rasterize the card until this resolves, otherwise the
    // capture can happen before the photo has loaded and pickAccentColor never gets to run.
    const resolvePhotoReadyRef = useRef();
    const photoReadyRef = useRef();
    if (!photoReadyRef.current) {
        photoReadyRef.current = new Promise((resolve) => {
            resolvePhotoReadyRef.current = resolve;
        });
    }
    const handlePhotoLoad = (event) => {
        var _a;
        try {
            const canvas = document.createElement("canvas");
            canvas.width = SAMPLE_SIZE;
            canvas.height = SAMPLE_SIZE;
            const context = canvas.getContext("2d");
            if (!context) {
                return;
            }
            context.drawImage(event.currentTarget, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
            setAccentColor(pickAccentColor(context.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data));
        }
        catch (_b) {
            // Sampling fails when the image host doesn't send CORS headers; keep the default accent.
        }
        finally {
            (_a = resolvePhotoReadyRef.current) === null || _a === void 0 ? void 0 : _a.call(resolvePhotoReadyRef);
        }
    };
    const handlePhotoError = () => {
        var _a;
        (_a = resolvePhotoReadyRef.current) === null || _a === void 0 ? void 0 : _a.call(resolvePhotoReadyRef);
    };
    useImperativeHandle(ref, () => ({
        toBlob: async () => {
            if (!cardRef.current) {
                return null;
            }
            await photoReadyRef.current;
            const fontEmbedCSS = await getFontEmbedCSS();
            return toBlob(cardRef.current, { fontEmbedCSS });
        },
        download: async (filename = "chuffed-share-image") => {
            if (!cardRef.current) {
                return;
            }
            await photoReadyRef.current;
            const fontEmbedCSS = await getFontEmbedCSS();
            const blob = await toBlob(cardRef.current, { fontEmbedCSS });
            if (!blob) {
                return;
            }
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = `${filename}.png`;
            link.click();
            URL.revokeObjectURL(blobUrl);
        },
    }), []);
    return (_jsxs("div", { className: `share-image-card${postClass}`, ref: cardRef, "data-testid": "share-image-card", children: [_jsx("img", { src: config.shareItem.imageUrl, alt: config.shareItem.caption, className: "share-image-card__photo", crossOrigin: "anonymous", onLoad: handlePhotoLoad, onError: handlePhotoError }), _jsx("div", { className: "share-image-card__headline", children: _jsxs("div", { className: "share-image-card__content", children: [_jsxs("p", { className: `share-image-card__text${postClass}`, children: [config.shareItem.caption, config.inlineCampaign && (_jsxs(_Fragment, { children: [" ", _jsx("span", { className: `share-image-card__primary-text --${accentColor}`, children: config.shareItem.campaignTitle })] }))] }), !config.inlineCampaign && (_jsx("p", { className: `share-image-card__headline-campaign --${accentColor}${postClass}`, children: config.shareItem.campaignTitle }))] }) }), config.showQrCode && (_jsx("div", { className: "share-image-card__qr", children: _jsx(QRCode, { value: config.shareUrl, enableCORS: true, quietZone: 0, eyeRadius: 10, fgColor: "#333333", size: QR_CODE_SIZE }) })), !config.showQrCode && config.showLinkPlaceholder && (_jsx("div", { className: "share-image-card__link-placeholder", "data-testid": "share-image-card-link-placeholder", children: _jsx("span", { children: "place story link here" }) }))] }));
});
export default ShareImageCard;
